import type { ApiClient, ApiError } from "../../shared/api/client";

const STALE_DRAFT_DETAIL = "stale draft revision";

function apiErrorOf(cause: unknown): Partial<ApiError> | undefined {
  return cause instanceof Error
    ? (cause.cause as Partial<ApiError> | undefined)
    : undefined;
}

/**
 * An interface edit is a draft based on the published version
 * (`base_version_id`). A missing or null base is never an interface edit.
 */
export function isInterfaceEditDraft(
  draftLoaded: boolean,
  baseVersionId?: string | null,
): boolean {
  return draftLoaded && typeof baseVersionId === "string";
}

/** The server no longer has the draft: it is already gone. */
export function isDraftMissing(cause: unknown): boolean {
  return apiErrorOf(cause)?.status === 404;
}

/** Publishing lost the race against a newer draft revision. */
export function isStaleDraftRevision(cause: unknown): boolean {
  const detail = apiErrorOf(cause);
  return (
    detail?.status === 409 &&
    (detail.code === STALE_DRAFT_DETAIL ||
      (cause instanceof Error && cause.message === STALE_DRAFT_DETAIL))
  );
}

export type DiscardOutcome = "deleted" | "already_gone" | "stale";

/**
 * Discard the draft revision the user is looking at. A 404 means someone
 * already did (the goal); a 409 means the draft changed meanwhile.
 */
export async function discardInterfaceDraft(
  api: Pick<ApiClient, "deleteDraft">,
  abilityId: string,
  expectedRevision?: number,
): Promise<DiscardOutcome> {
  try {
    await api.deleteDraft(abilityId, expectedRevision);
    return "deleted";
  } catch (cause: unknown) {
    if (isDraftMissing(cause)) return "already_gone";
    if (isStaleDraftRevision(cause)) return "stale";
    throw cause;
  }
}
